import { Content } from "@jobber/components/Content";
import { Text } from "@jobber/components/Text";
import { Heading } from "@jobber/components/Heading";
import { Emphasis } from "@jobber/components/Emphasis";
import { RadioGroup, RadioOption } from "@jobber/components/RadioGroup";
import { InputText } from "@jobber/components/InputText";
import { InvoiceMappingField } from "../../types";

type InvoiceMappingProps = {
    field: InvoiceMappingField;
    prefix: string;
    onFieldChange: (field: InvoiceMappingField) => void;
    onPrefixChange: (prefix: string) => void;
};

const InvoiceMapping = ({ field, prefix, onFieldChange, onPrefixChange }: InvoiceMappingProps) => {
    return (
        <Content type={"div"} spacing={"base"}>
            <Heading level={2}>Invoice mapping</Heading>
            <Text variation="subdued">
                Choose how to display invoice numbers when syncing an invoice to Xero. Please note that changes will only apply to future synced invoices; previously synced invoices will be unaffected.
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "480px" }}>
                    <Heading level={4}>Sync invoices to Xero using</Heading>
                    <RadioGroup
                        ariaLabel={"Sync invoices to Xero using"}
                        value={field}
                        onChange={(value) => onFieldChange(value as InvoiceMappingField)}
                    >
                        <RadioOption value={InvoiceMappingField.Id}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingBottom: "8px" }}>
                                <Text><Emphasis variation={"bold"}>Jobber's internal invoice ID</Emphasis></Text>
                                <Text variation={"subdued"}>Ex. JOBBER-123456789. This is a permanent identifier that never changes and is always unique, so you can be sure it won't result in syncing issues.</Text>
                            </div>
                        </RadioOption>
                        <RadioOption value={InvoiceMappingField.Number}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingBottom: "8px" }}>
                                <Text><Emphasis variation={"bold"}>Jobber's external invoice number</Emphasis></Text>
                                <Text variation={"subdued"}>Ex. JOBBER-123. This is the actual "Invoice #" field on Jobber, so it's more recognizable — but it's editable and not necessarily unique, so can result in syncing issues.</Text>
                            </div>
                        </RadioOption>
                    </RadioGroup>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <Heading level={4}>Sync invoices to Xero with this prefix</Heading>
                    <Text variation={"subdued"}>
                        A prefix identifies invoices as originating from Jobber, and reduces the chance of conflict with existing invoice numbers on Xero. The prefix can be customized or removed entirely, but please be aware this may result in syncing issues that have to be manually resolved.
                    </Text>
                    <div style={{ maxWidth: "260px" }}>
                        <InputText
                            value={prefix}
                            onChange={(value) => onPrefixChange(value as string)}
                        />
                    </div>
                </div>
            </div>
        </Content>
    );
};

export default InvoiceMapping;
